import { parseEnvelope } from "@personnel/core";
import { leviathanRules } from "@personnel/rules-leviathan";
import type { LeviathanCaptain } from "@personnel/rules-leviathan";

export interface ImportedRecord {
  captain: LeviathanCaptain;
  /** Ruleset the file was written by, when it differs from the current one. */
  writtenBy: string | null;
}

/**
 * Reads a record exported by this tool back into a captain.
 *
 * Records carry the ruleset that wrote them, so a file from an older version
 * still loads: fields added since then take their schema defaults rather than
 * failing to parse. A file from a *different* ruleset is refused outright --
 * a pilot record is not a captain, and silently coercing one would be worse
 * than saying so.
 */
export async function importCaptainRecord(file: File): Promise<ImportedRecord> {
  let raw: unknown;
  try {
    raw = JSON.parse(await file.text());
  } catch {
    throw new Error(`${file.name} is not valid JSON.`);
  }

  let envelope;
  try {
    envelope = parseEnvelope(raw);
  } catch (cause) {
    throw new Error(
      cause instanceof Error
        ? `${file.name} is not a personnel record — ${cause.message}`
        : `${file.name} is not a personnel record.`,
    );
  }

  if (envelope.rulesetId !== leviathanRules.id) {
    throw new Error(
      `${file.name} was written for the ${envelope.rulesetId} ruleset. Open it with that builder instead.`,
    );
  }

  const parsed = leviathanRules.schema.safeParse(envelope.data);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    throw new Error(
      first === undefined
        ? `${file.name} does not hold a readable captain.`
        : `${file.name} does not hold a readable captain: ${first.path.join(".") || "record"} ${first.message.toLowerCase()}.`,
    );
  }

  return {
    captain: parsed.data,
    writtenBy:
      envelope.rulesetVersion === leviathanRules.version ? null : envelope.rulesetVersion,
  };
}
